import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api/client";
import type { DiscoverItem, DiscoverStats } from "../api/types";
import EmptyState from "../components/EmptyState";
import Footer from "../components/Footer";
import PageHeader from "../components/PageHeader";
import StanceBadge from "../components/StanceBadge";
import TopNav from "../components/TopNav";

type Horizon = "short" | "medium" | "long" | "all";
type Side = "bullish" | "bearish" | "both";

const HORIZONS: { id: Horizon; label: string }[] = [
  { id: "short", label: "Short" },
  { id: "medium", label: "Medium" },
  { id: "long", label: "Long" },
  { id: "all", label: "All" },
];

const MIN_SCORES = [65, 70, 75] as const;

function fmtCap(cap: number | null | undefined): string {
  if (cap == null) return "—";
  if (cap >= 1e12) return `$${(cap / 1e12).toFixed(2)}T`;
  if (cap >= 1e9) return `$${(cap / 1e9).toFixed(1)}B`;
  if (cap >= 1e6) return `$${(cap / 1e6).toFixed(0)}M`;
  return `$${cap}`;
}

function fmtRelative(iso: string | null): string {
  if (!iso) return "never";
  const dt = new Date(iso).getTime();
  const diff = Date.now() - dt;
  const h = Math.floor(diff / 3_600_000);
  if (h < 1) {
    const m = Math.max(1, Math.floor(diff / 60_000));
    return `${m}m ago`;
  }
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function ComponentChip({ label, value }: { label: string; value: string }) {
  return (
    <span className="text-[10px] text-slate-500">
      {label} <span className="text-slate-400">{value}</span>
    </span>
  );
}

function ComponentSummary({ components }: { components: Record<string, unknown> }) {
  const llm = components["llm"] as number | undefined;
  const mom = components["momentum"] as number | undefined;
  const insiders = components["insider_cluster_filers"] as number | undefined;
  const div = components["dividend"] as number | undefined;

  const chips: { label: string; value: string }[] = [];
  if (typeof llm === "number") chips.push({ label: "llm", value: llm.toFixed(2) });
  if (typeof mom === "number") {
    const pct = (mom * 100).toFixed(0);
    chips.push({ label: "mom", value: `${mom >= 0 ? "+" : ""}${pct}%` });
  }
  if (typeof insiders === "number" && insiders > 0)
    chips.push({ label: "ins", value: String(insiders) });
  if (typeof div === "number" && div !== 0) chips.push({ label: "div", value: div.toFixed(2) });

  if (chips.length === 0) return null;
  return (
    <div className="mt-1 flex flex-wrap items-baseline gap-x-3">
      {chips.map((c) => (
        <ComponentChip key={c.label} {...c} />
      ))}
    </div>
  );
}

function AddWatchButton({
  yahoo,
  ticker,
  onAdded,
}: {
  yahoo: string;
  ticker: string;
  onAdded: () => void;
}) {
  const m = useMutation({
    mutationFn: async () => {
      await api.post("/watchlist/items", { yahoo_symbol: yahoo, tier: "tracked" });
    },
    onSuccess: onAdded,
  });
  return (
    <button
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        m.mutate();
      }}
      disabled={m.isPending}
      title={`Add ${ticker} to watchlist`}
      aria-label={`Add ${ticker} to watchlist`}
      className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-sky-500/15 text-sky-300 ring-1 ring-sky-500/30 transition hover:bg-sky-500/30 disabled:opacity-50"
    >
      {m.isPending ? (
        <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4 animate-spin" aria-hidden="true">
          <circle cx="10" cy="10" r="7" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" />
          <path d="M17 10a7 7 0 0 0-7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      ) : (
        <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
          <path d="M10 4a.75.75 0 0 1 .75.75v4.5h4.5a.75.75 0 0 1 0 1.5h-4.5v4.5a.75.75 0 0 1-1.5 0v-4.5h-4.5a.75.75 0 0 1 0-1.5h4.5v-4.5A.75.75 0 0 1 10 4Z" />
        </svg>
      )}
    </button>
  );
}

export default function Discover() {
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();

  const horizon = (sp.get("horizon") as Horizon | null) ?? "medium";
  const minScore = parseInt(sp.get("min_score") ?? "65", 10);
  const antiTrending = sp.get("anti_trending") !== "false";
  const insiderGated = sp.get("insider_gated") === "true";
  const side = (sp.get("side") as Side | null) ?? "bullish";

  function updateParam(key: string, value: string | null) {
    const next = new URLSearchParams(sp);
    if (value === null) next.delete(key);
    else next.set(key, value);
    setSp(next, { replace: true });
  }

  const list = useQuery<DiscoverItem[]>({
    queryKey: ["discover", { horizon, minScore, antiTrending, insiderGated, side }],
    queryFn: async () =>
      (
        await api.get<DiscoverItem[]>("/discover", {
          params: {
            horizon,
            min_score: minScore,
            anti_trending: antiTrending,
            insider_gated: insiderGated,
            side,
            limit: 100,
          },
        })
      ).data,
    staleTime: 30_000,
  });

  const stats = useQuery<DiscoverStats>({
    queryKey: ["discover", "stats"],
    queryFn: async () => (await api.get<DiscoverStats>("/discover/stats")).data,
    staleTime: 60_000,
  });

  const items = list.data ?? [];

  const refresh = useMemo(
    () => fmtRelative(stats.data?.last_refresh_at ?? null),
    [stats.data?.last_refresh_at],
  );

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-6">
      <PageHeader />
      <TopNav />

      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h1 className="text-lg font-medium text-slate-200">Discover</h1>
        <div className="text-[11px] text-slate-500">
          <span className="text-slate-400">{stats.data?.n_eligible ?? "—"}</span> eligible · last
          refresh <span className="text-slate-400">{refresh}</span>
        </div>
      </div>

      <p className="mb-4 text-xs leading-relaxed text-slate-500">
        AI-ranked picks from the S&amp;P 500 + Nasdaq 100, filtered to large-cap, liquid, mature
        names. Toggle anti-trending off to also include names with high social attention. Off your
        watchlist — promote ones you want to follow.
      </p>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="-mx-1 flex gap-1 overflow-x-auto px-1">
          {HORIZONS.map((h) => (
            <button
              key={h.id}
              onClick={() => updateParam("horizon", h.id === "medium" ? null : h.id)}
              className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition ${
                horizon === h.id
                  ? "bg-slate-800 text-slate-100 ring-1 ring-slate-700"
                  : "text-slate-400 hover:bg-slate-800/40 hover:text-slate-200"
              }`}
            >
              {h.label}
            </button>
          ))}
        </div>

        <div className="ml-2 flex gap-1">
          {MIN_SCORES.map((s) => (
            <button
              key={s}
              onClick={() => updateParam("min_score", s === 65 ? null : String(s))}
              className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition ${
                minScore === s
                  ? "bg-slate-800 text-slate-100 ring-1 ring-slate-700"
                  : "text-slate-500 hover:bg-slate-800/40 hover:text-slate-200"
              }`}
            >
              ≥{s}
            </button>
          ))}
        </div>

        <button
          onClick={() => updateParam("anti_trending", antiTrending ? "false" : null)}
          className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
            antiTrending
              ? "bg-sky-500/15 text-sky-300 ring-1 ring-sky-500/30"
              : "text-slate-500 hover:bg-slate-800/40 hover:text-slate-200"
          }`}
          title="Exclude symbols in the top-50 trending snapshot"
        >
          ✓ anti-trending
        </button>

        <button
          onClick={() => updateParam("insider_gated", insiderGated ? null : "true")}
          className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
            insiderGated
              ? "bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/30"
              : "text-slate-500 hover:bg-slate-800/40 hover:text-slate-200"
          }`}
          title="Show only score ≥70 with ≥2 distinct insider cluster filers"
        >
          insider-gated
        </button>

        {side !== "bullish" && (
          <button
            onClick={() => updateParam("side", null)}
            className="rounded-full px-3 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-800/40 hover:text-slate-200"
          >
            ↺ reset
          </button>
        )}
      </div>

      {list.isError && (
        <EmptyState
          variant="error"
          title="Couldn't load Discover"
          hint="Try again — the eligibility cron runs nightly at 04:50 UTC."
          action={
            <button
              onClick={() => list.refetch()}
              className="rounded-lg border border-rose-800 px-3 py-1.5 text-xs text-rose-200 transition hover:bg-rose-900/40"
            >
              Retry
            </button>
          }
        />
      )}

      {!list.isError && list.isLoading && <EmptyState variant="loading" title="Loading…" />}

      {!list.isError && !list.isLoading && items.length === 0 && (
        <EmptyState
          title="No matches"
          hint={
            (stats.data?.n_eligible ?? 0) === 0
              ? "Discover universe is loading — eligibility pass runs nightly at 04:50 UTC."
              : "No symbols pass these filters today. Try lowering the minimum score, broadening the horizon, or toggling insider-gated off."
          }
        />
      )}

      {items.length > 0 && (
        <section className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900">
          <ul className="divide-y divide-slate-800">
            {items.map((it, i) => (
              <li key={`${it.symbol_id}-${it.horizon}`}>
                <Link
                  to={`/s/${encodeURIComponent(it.yahoo_symbol)}`}
                  className="block hover:bg-slate-800/40"
                >
                  <div className="flex items-start gap-3 px-3 py-3 sm:px-4">
                    <div className="w-6 shrink-0 pt-0.5 text-right text-xs font-medium tabular-nums text-slate-600">
                      {i + 1}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-2">
                        <span className="font-mono text-base text-slate-100">{it.ticker}</span>
                        {it.horizon !== horizon && horizon === "all" && (
                          <span className="text-[10px] uppercase tracking-wide text-slate-600">
                            {it.horizon}
                          </span>
                        )}
                        <StanceBadge stance={it.stance as "bullish" | "neutral" | "bearish"} />
                        <span className="text-[10px] text-slate-600">{fmtCap(it.market_cap)}</span>
                        {it.sector && (
                          <span className="text-[10px] text-slate-600">{it.sector}</span>
                        )}
                      </div>
                      <div className="truncate text-xs text-slate-500">{it.name}</div>
                      <ComponentSummary components={it.components} />
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <div className="text-right">
                        <div className="font-mono text-lg leading-none tabular-nums text-slate-100">
                          {it.score.toFixed(1)}
                        </div>
                        <div className="text-[10px] uppercase tracking-wide text-slate-600">
                          score
                        </div>
                      </div>
                      <AddWatchButton
                        yahoo={it.yahoo_symbol}
                        ticker={it.ticker}
                        onAdded={() => {
                          qc.invalidateQueries({ queryKey: ["discover"] });
                          qc.invalidateQueries({ queryKey: ["watchlist"] });
                        }}
                      />
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <Footer note="Discover refreshes after each rescore (every 30 min) · Universe re-evaluated nightly at 04:50 UTC" />
    </div>
  );
}
